import React, { useState } from 'react';
import { BrandedButton } from './BrandedButton';
import { BrandedInput } from './BrandedInput';
import { KratosFlow, KratosNode } from '../services/kratosService';
import './TwoFactorSettings.css';

interface TwoFactorSettingsProps {
  flow: KratosFlow;
  /** Submit one settings method; resolves once the parent has the new flow. */
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy: boolean;
}

const nodesOf = (flow: KratosFlow, group: string): KratosNode[] =>
  flow.ui.nodes.filter((node) => node.group === group);

const find = (nodes: KratosNode[], name: string): KratosNode | undefined =>
  nodes.find((node) => node.attributes?.name === name || node.attributes?.id === name);

/** Kratos sends button values as true or "true". */
const buttonValue = (node: KratosNode): unknown =>
  node.attributes.value === 'true' ? true : node.attributes.value ?? true;

/**
 * Two-factor authentication, driven by the Kratos settings flow: which
 * controls appear depends on the nodes Kratos sends for the account's state
 * (authenticator app linked or not; backup codes none / freshly generated /
 * saved). Nothing is shown for a method Kratos doesn't offer.
 */
export const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({ flow, onSubmit, busy }) => {
  const [totpCode, setTotpCode] = useState('');

  const totp = nodesOf(flow, 'totp');
  const lookup = nodesOf(flow, 'lookup_secret');
  if (totp.length === 0 && lookup.length === 0) return null;

  const qr = totp.find((node) => node.type === 'img');
  const secret = find(totp, 'totp_secret_key');
  const unlink = find(totp, 'totp_unlink');
  const codeInput = find(totp, 'totp_code');

  const codes = find(lookup, 'lookup_secret_codes');
  const secrets = codes?.attributes.text?.context?.secrets ?? [];
  const lookupButtons = lookup.filter(
    (node) => node.type === 'input' && ['submit', 'button'].includes(node.attributes.type)
  );

  const errors = [...totp, ...lookup]
    .flatMap((node) => node.messages ?? [])
    .filter((message) => message.type === 'error');

  const enrolTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit({ method: 'totp', totp_code: totpCode.trim() });
    setTotpCode('');
  };

  return (
    <section className="settings-form" aria-labelledby="two-factor-title">
      <h3 id="two-factor-title" className="settings-section-title">
        Two-factor authentication
      </h3>

      {errors.map((message) => (
        <div key={message.id} className="settings-error" role="alert">
          {message.text}
        </div>
      ))}

      {unlink ? (
        <>
          <p className="settings-help">
            An authenticator app is set up. Signing in asks for the 6-digit code it shows.
          </p>
          <BrandedButton
            type="button"
            variant="outline"
            fullWidth
            disabled={busy}
            onClick={() => onSubmit({ method: 'totp', totp_unlink: true })}
          >
            Remove authenticator app
          </BrandedButton>
        </>
      ) : codeInput ? (
        <form onSubmit={enrolTotp} className="settings-two-factor">
          <p className="settings-help">
            Scan the QR code with an authenticator app (for example Google Authenticator,
            Microsoft Authenticator, 1Password or Authy), then enter the 6-digit code it shows.
          </p>
          {qr?.attributes.src && (
            <img
              className="settings-qr"
              src={qr.attributes.src}
              alt="QR code to add QAIM to your authenticator app"
              width={180}
              height={180}
            />
          )}
          {secret?.attributes.text?.text && (
            <p className="settings-help">
              Can&apos;t scan it? Enter this key instead:{' '}
              <code className="settings-secret">{secret.attributes.text.text}</code>
            </p>
          )}
          <BrandedInput
            type="text"
            name="totp_code"
            label="Code from the app"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value)}
            placeholder="6-digit code"
            autoComplete="one-time-code"
            required
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={busy}>
            Turn on two-factor authentication
          </BrandedButton>
        </form>
      ) : null}

      {lookup.length > 0 && (
        <div className="settings-backup-codes">
          <h4 className="settings-subsection-title">Backup codes</h4>
          <p className="settings-help">
            One-time codes for signing in when you don&apos;t have your authenticator app. Each
            code works once.
          </p>
          {secrets.length > 0 && (
            <>
              <p className="settings-help">
                Save these somewhere safe (a password manager, or printed). They won&apos;t be
                shown again unless you reveal them.
              </p>
              <ul className="settings-codes" aria-label="Backup codes">
                {secrets.map((code) => (
                  <li key={code.text} className={code.context?.used_at ? 'used' : undefined}>
                    <code>{code.text}</code>
                    {code.context?.used_at ? ' (used)' : null}
                  </li>
                ))}
              </ul>
            </>
          )}
          {lookupButtons.map((node) => (
            <BrandedButton
              key={node.attributes.name}
              type="button"
              variant={node.attributes.name === 'lookup_secret_confirm' ? 'primary' : 'outline'}
              fullWidth
              disabled={busy}
              onClick={() =>
                onSubmit({ method: 'lookup_secret', [node.attributes.name]: buttonValue(node) })
              }
            >
              {node.meta?.label?.text ?? node.attributes.name}
            </BrandedButton>
          ))}
        </div>
      )}
    </section>
  );
};
